import { useState, useEffect, useRef } from "react";
import { getHeroContent, DEFAULT_HERO } from "@/lib/content";
import type { HeroContent } from "@/types/content";
import { getOptimizedImageUrl } from "@/lib/utils";

const HeroSection = () => {
  const [hero, setHero] = useState<HeroContent>(DEFAULT_HERO);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [prevImageIndex, setPrevImageIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const ticking = useRef(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile, { passive: true });
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Throttled scroll handler to avoid forced reflow
  useEffect(() => {
    if (isMobile) return;
    const handleScroll = () => {
      if (!ticking.current) {
        requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking.current = false;
        });
        ticking.current = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  useEffect(() => {
    getHeroContent(setHero).then((data) => {
      setHero(data);
    });
  }, []);

  useEffect(() => {
    if (!hero || !hero.heroImageUrls || hero.heroImageUrls.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => {
        setPrevImageIndex(prev);
        return (prev + 1) % hero.heroImageUrls.length;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [hero]);

  // CSS-based scroll transforms (no framer-motion forced reflow)
  const scrollProgress = Math.min(scrollY / 200, 1);
  const scale = 1 - scrollProgress * 0.04;
  const borderRadius = scrollProgress * 40;
  const marginH = scrollProgress * 16;

  const sectionStyle = isMobile ? {} : {
    transform: `scale(${scale})`,
    borderRadius: `${borderRadius}px`,
    marginLeft: `${marginH}px`,
    marginRight: `${marginH}px`,
    willChange: 'transform',
    transition: 'transform 0.1s linear, border-radius 0.1s linear, margin 0.1s linear',
  };

  return (
    <div className="bg-white overflow-visible">
      <section
        style={sectionStyle}
        className="relative w-full aspect-[16/9] md:aspect-auto md:h-[calc(100vh-104px)] flex items-center overflow-hidden bg-slate-100"
      >
        {/* Background Image Slider — pure CSS transitions */}
        <div className="absolute inset-0 z-0">
          {hero.heroImageUrls?.map((url, i) => (
            <img
              key={i}
              src={getOptimizedImageUrl(url, isMobile)}
              alt={i === 0 ? "Daily Demand Premium Baby Care" : `Daily Demand slide ${i + 1}`}
              width="1920"
              height="1080"
              fetchPriority={i === 0 ? "high" : "low"}
              loading={i === 0 ? "eager" : "lazy"}
              decoding={i === 0 ? "sync" : "async"}
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                opacity: i === currentImageIndex ? 1 : 0,
                transition: i === currentImageIndex || i === prevImageIndex
                  ? 'opacity 1.5s ease-in-out'
                  : 'none',
              }}
            />
          ))}
          {/* Fallback if no images loaded */}
          {(!hero.heroImageUrls || hero.heroImageUrls.length === 0) && (
            <img
              src="https://placehold.co/1920x1080/e6f0fa/3561a6?text=Premium+Baby+Care"
              alt="Daily Demand Premium Baby Care"
              width="1920"
              height="1080"
              fetchPriority="high"
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
        </div>

        {/* Explore Button */}
        <div className="absolute inset-0 z-10 flex items-end justify-center pb-8 md:pb-24">
          <button
            onClick={() => document.getElementById('products-list')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#006C73]/90 backdrop-blur-sm text-white px-3 py-1 md:px-8 md:py-3 rounded-full font-black text-[8px] md:text-[10px] uppercase tracking-[0.15em] md:tracking-[0.2em] hover:bg-white hover:text-[#006C73] transition-all duration-300 shadow-2xl active:scale-95 animate-fadeIn"
            style={{ animationDelay: '1s', opacity: 0, animationFillMode: 'forwards' }}
          >
            Explore Collection
          </button>
        </div>

        {/* Slider Indicators */}
        {hero.heroImageUrls && hero.heroImageUrls.length > 1 && (
          <div className="absolute bottom-2 md:bottom-10 left-0 right-0 flex justify-center gap-4 z-10">
            {hero.heroImageUrls.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setPrevImageIndex(currentImageIndex);
                  setCurrentImageIndex(i);
                }}
                className="group p-2"
                aria-label={`Go to slide ${i + 1}`}
              >
                <div className={`h-1 rounded-full transition-all duration-500 ${
                  i === currentImageIndex ? "bg-[#006C73] w-12" : "bg-white/30 group-hover:bg-white/60 w-6"
                }`} />
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default HeroSection;


